/**
 * useEditProfile — manages edit-profile form state.
 * Mirrors Flutter WebEditProfileBloc:
 *   - init → load candidate details + dynamic form schema
 *   - setFldInput → populate form fields from candidate data
 *   - onEditProfile → build updated CandidateDetails and PUT
 *   - resume upload / download within edit profile
 * plus WebProfileBloc's session-expiry logout (getUserDetails → sessionExpired → onLogout).
 *
 * Flutter source:
 *   lib/application/web_profile/web_edit_profile/web_edit_profile_bloc.dart
 *   lib/application/web_profile/web_profile_bloc.dart (_GetUserDetails, _OnLogout)
 *   lib/presentation/web_profile/web_profile.dart (isLoggedOut listener)
 *   lib/presentation/common/form_util.dart (BuildFormField validators)
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { auth } from '@/config/firebase';
import { ROUTES } from '@/app/router/routes';
import {
  SESSION_EXPIRED_ERROR,
  candidateDetailsFromJson,
  candidateDetailsToJson,
  getCandidateDetails,
  updateCandidateDetails,
} from '@/services/api/profileApi';
import {
  EMPTY_FILE_ERROR,
  getPlaceSuggestions,
  uploadResumeStandalone,
  downloadResume,
} from '@/services/api/applicationApi';
import { getEditProfileFormContent } from '@/services/api/authApi';
import { signOutUser } from '@/services/auth/authService';
import { analyticsService } from '@/services/analytics/analyticsService';
import { useAppStateStore } from '@/store/appStateStore';
import { useTenantStore } from '@/store/tenantStore';
import type { CandidateDetails } from '@/types/profile.types';
import type { FldListState, FldListJson, ComponentJson, OptionData } from '@/types/application.types';
import { FieldType } from '@/types/application.types';
import { parseFldJson } from '@/features/application/utils/formHelpers';
import { showToast } from '@/utils/globalToast';
import { validateResumeFile } from '@/features/jobs/utils/resumeFile';
import { DEFAULT_PHONE_CODE } from '@/config/countryCodes';

// Flutter ErrorConstants / AppConstants texts (Toast translates them)
const GENERIC_ERROR = 'Something went wrong, Please try again!';
const LOGOUT_FAILED = 'Failed to logout. Please try again!';
const FILL_ALL_MANDATORY_FIELDS = 'some mandatory fields are either not filled or selected';
const PROFILE_UPDATED = 'Profile Updated Successfully';
const RESUME_UPLOADED = 'Resume uploaded successfully';
const NOTICE_PERIOD_TOO_LONG = 'Days should be less than 180 days.';

export interface EditProfileState {
  candidateDetails: CandidateDetails | null;
  fields: FldListState[];
  /** Initial load (schema + candidate) */
  isLoading: boolean;
  /** Save / upload / download in progress — Flutter ModalProgressHUD over the form */
  isBusy: boolean;
  enableSave: boolean;
  /** Field key → error, shown after a save attempt */
  errors: Record<string, string>;
  errorMessage: string;
  successMessage: string;
}

/* ────────────────────────────── Experience ────────────────────────────── */

const isExpYearsField = (f: FldListState) => f.subType === 'EXP' && !!f.title;

/** Option keys built by parseFldJson: "0 years", "1 year", "2 years" … */
const yearsKey = (n: number) => `${n} ${n > 1 || n === 0 ? 'years' : 'year'}`;
const monthsKey = (n: number) => `${n} ${n > 1 || n === 0 ? 'months' : 'month'}`;

const digitsOf = (value: string) => parseInt(value.replace(/[^0-9]/g, ''), 10);

/* ────────────────────────────── Populate (Flutter _SetFldInput) ────────────────────────────── */

function populateFieldsFromCandidate(
  fields: FldListState[],
  candidate: CandidateDetails,
): FldListState[] {
  const candidateJson = candidate as Record<string, unknown>;
  const populated = fields.map((f) => ({ ...f }));

  for (let i = 0; i < populated.length; i++) {
    const fld = populated[i];

    if (fld.type === FieldType.TEXT_FLD || fld.type === FieldType.SEARCH_TEXT_FLD) {
      fld.value = (candidateJson[fld.key] as string) ?? '';
      fld.displayValue = fld.value;
      if (fld.key === 'primaryContactNumber') {
        fld.countryCode = candidate.primaryContactNumberCountryCode || DEFAULT_PHONE_CODE;
      } else if (fld.key === 'secondaryContactNumber') {
        fld.countryCode =
          candidate.secondaryContactNumberCountryCode ||
          candidate.primaryContactNumberCountryCode ||
          DEFAULT_PHONE_CODE;
      }
      // Incase empty we are enabling (Flutter)
      if (fld.key === 'primaryContactNumber' && !fld.value) {
        fld.isMandatory = true;
        fld.isDisabled = false;
      }
    } else if (isExpYearsField(fld)) {
      // Flutter GenericHelpers.getMonthsToYearArray — 0 months leaves both dropdowns empty
      const expInMonths = Number(candidateJson['experienceInMonths'] ?? 0) || 0;
      const next = populated[i + 1];
      if (expInMonths > 0) {
        fld.value = yearsKey(Math.floor(expInMonths / 12));
        if (next) next.value = monthsKey(expInMonths % 12);
      } else {
        fld.value = '';
        if (next) next.value = '';
      }
    }
  }

  return populated;
}

/* ────────────────────────────── Validation (Flutter FormValidators) ────────────────────────────── */

// Flutter FormValidators.isValidEmail regex
const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

function validateProfileField(
  field: FldListState,
  primaryEmailId: string,
  t: TFunction,
): string | null {
  const value = field.value ?? '';

  if (field.subType === 'EXP') {
    // SearchDropDown validator
    if (field.isMandatory && !value) {
      return `${field.errorText || field.title} ${t('is required')}`;
    }
    return null;
  }

  if (field.type === FieldType.TEXT_FLD) {
    // Mobile: Flutter validateMobile gets '+cc', which never matches its digit-size table, so a
    // filled number always passes (the min-length check below never runs for it).
    if (field.isMobileFld && value) return null;
    if (field.isEmailFld) {
      const isAlternate = field.key === 'secondaryEmailId';
      if (isAlternate && !value) return null;
      if (!EMAIL_REGEX.test(value)) return t('Not a valid email');
      if (isAlternate && primaryEmailId && value.toLowerCase() === primaryEmailId.toLowerCase()) {
        return t('Primary Email and Alternate Email cannot be same');
      }
      return null;
    }
    if (field.key === 'noticePeriodInDays' && value && parseInt(value, 10) > 180) {
      return t(NOTICE_PERIOD_TOO_LONG);
    }
    if (field.isMandatory) {
      const minLen = ['noticePeriodInDays', 'firstName', 'lastName'].includes(field.key) ? 1 : 2;
      if (!value) return `${field.title} ${t('is required')}`;
      if (value.length < minLen) {
        return `${field.title} ${t('must have at least')} ${minLen} ${t('characters')}`;
      }
    }
    return null;
  }

  if (field.type === FieldType.SEARCH_TEXT_FLD && field.isMandatory && !value) {
    return `${field.title} ${t('is required')}`;
  }

  return null;
}

function collectErrors(
  fields: FldListState[],
  primaryEmailId: string,
  t: TFunction,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const fld of fields) {
    const err = validateProfileField(fld, primaryEmailId, t);
    if (err) errors[fld.key] = err;
  }
  return errors;
}

/* ────────────────────────────── Build payload (Flutter buildCandidateDetails) ────────────────────────────── */

function buildCandidateDetails(
  candidate: CandidateDetails | null,
  fields: FldListState[],
): CandidateDetails {
  // Flutter starts from state.candidateDetails.toJson(), the model parsed from GET v1/candidate.
  // It also re-reads candidateUrl / resumeUpdateOn from AppStateNotifier; here the candidate is
  // refetched after a resume upload, so it already carries them.
  const candidateJson = candidateDetailsToJson(candidateDetailsFromJson(candidate ?? {}));

  for (let i = 0; i < fields.length; i++) {
    const fld = fields[i];

    if (isExpYearsField(fld)) {
      let totalInMonths = 0;
      if (fld.value) {
        const years = digitsOf(fld.value);
        if (!isNaN(years)) totalInMonths = years * 12;
      }
      const monthsFld = fields[i + 1];
      if (monthsFld?.value) {
        const months = digitsOf(monthsFld.value);
        if (!isNaN(months)) totalInMonths += months;
      }
      candidateJson['experienceInMonths'] = totalInMonths;
    } else if (fld.key && fld.type === FieldType.TEXT_FLD) {
      candidateJson[fld.key] = fld.value ?? '';
      if (fld.key === 'secondaryContactNumber') {
        candidateJson['secondaryContactNumberCountryCode'] = fld.countryCode;
      } else if (fld.key === 'primaryContactNumber') {
        candidateJson['primaryContactNumberCountryCode'] = fld.countryCode;
      }
    } else if (fld.key && fld.subType !== 'EXP') {
      candidateJson[fld.key] = fld.value;
    }
  }

  // Flutter CandidateDetails.fromJson: form keys outside the model are dropped, nulls become ''/0
  return candidateDetailsFromJson(candidateJson);
}

/** Flutter _SetFldInput: Save starts enabled when any mandatory text field has a value. */
function hasMandatoryTextValue(fields: FldListState[]): boolean {
  return fields.some(
    (f) =>
      (f.type === FieldType.TEXT_FLD || f.type === FieldType.SEARCH_TEXT_FLD) &&
      f.isMandatory &&
      !!f.value,
  );
}

async function loadFormSchema(preferCache: boolean): Promise<ComponentJson | null> {
  if (preferCache) {
    const cached = localStorage.getItem('editProfileFormContent');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed?.basicInformation) return parsed.basicInformation as ComponentJson;
      } catch { /* ignore */ }
    }
  }
  try {
    const raw = (await getEditProfileFormContent()) as Record<string, unknown>;
    return (raw?.basicInformation as ComponentJson) ?? null;
  } catch {
    return null;
  }
}

export function useEditProfile() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const preferences = useTenantStore((s) => s.preferences);
  const content = useTenantStore((s) => s.content);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadedLanguageRef = useRef<string | null>(null);
  /** Bumped by reload() to re-run init and discard unsaved edits */
  const [reloadKey, setReloadKey] = useState(0);

  const [state, setState] = useState<EditProfileState>({
    candidateDetails: null,
    fields: [],
    isLoading: true,
    isBusy: false,
    enableSave: false,
    errors: {},
    errorMessage: '',
    successMessage: '',
  });

  const patch = useCallback(
    (partial: Partial<EditProfileState>) =>
      setState((prev) => ({ ...prev, ...partial })),
    [],
  );

  /**
   * Flutter WebProfileEvent.onLogout(message: sessionExpired) and the WebProfile isLoggedOut
   * listener: logout (+ deleteJobRefferalData), wait 1 s, log the logout, drop the cached form
   * schemas, go home, show the message and reload the page.
   */
  const logoutOnSessionExpiry = useCallback(
    async (message: string) => {
      patch({ isBusy: true });
      try {
        await signOutUser();
      } catch {
        patch({ isBusy: false, errorMessage: LOGOUT_FAILED });
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
      void analyticsService.logEvent('profile_menu_options', 'logout', message);
      // LocalDataService.deleteJobApplyFormContentData / deleteEditProfileFormContentData
      localStorage.removeItem('applyFormContent');
      localStorage.removeItem('editProfileFormContent');
      void analyticsService.logScreenView({ screenName: ROUTES.HOME });
      navigate(ROUTES.HOME);
      // The sign-out may already have unmounted this page (ProtectedRoute), so use the app overlay
      showToast(message, 'error');
      window.location.reload();
    },
    [navigate, patch],
  );
  const logoutOnSessionExpiryRef = useRef(logoutOnSessionExpiry);
  logoutOnSessionExpiryRef.current = logoutOnSessionExpiry;

  /* ── Init: load form schema + candidate details (re-runs on language change, like
        Flutter's languageStream reload — the schema is localized server-side) ── */
  const language = i18n.language;
  useEffect(() => {
    if (!auth.currentUser) return;
    let cancelled = false;
    const isLanguageChange =
      loadedLanguageRef.current !== null && loadedLanguageRef.current !== language;
    loadedLanguageRef.current = language;

    async function init() {
      patch({ isLoading: true, errors: {} });

      const formContent = await loadFormSchema(!isLanguageChange);
      if (cancelled) return;

      const rawFields: FldListState[] = (formContent?.fieldList ?? []).map((fld) =>
        parseFldJson(fld as FldListJson),
      );

      try {
        const candidate = await getCandidateDetails();
        if (cancelled) return;
        const populated = populateFieldsFromCandidate(rawFields, candidate);
        patch({
          candidateDetails: candidate,
          fields: populated,
          isLoading: false,
          enableSave: hasMandatoryTextValue(populated),
        });
      } catch (error) {
        if (cancelled) return;
        // Flutter WebProfileBloc._GetUserDetails: sessionExpired → onLogout(message)
        if (error instanceof Error && error.message === SESSION_EXPIRED_ERROR) {
          void logoutOnSessionExpiryRef.current(SESSION_EXPIRED_ERROR);
        }
        for (const fld of rawFields) {
          if (fld.key === 'primaryEmailId') {
            fld.isDisabled = false;
          }
          if (fld.key === 'primaryContactNumber') {
            fld.isDisabled = false;
            fld.isMandatory = true;
          }
        }
        patch({
          fields: rawFields,
          isLoading: false,
        });
      }
    }

    void init();
    return () => {
      cancelled = true;
    };
  }, [language, reloadKey, patch]);

  /* ── Field updates — Flutter Form.onChanged: enableSave = form validates ── */
  const applyFieldChange = useCallback(
    (index: number, changes: Partial<FldListState>) => {
      setState((prev) => {
        const fields = [...prev.fields];
        fields[index] = { ...fields[index], ...changes };
        const primaryEmailId = prev.candidateDetails?.primaryEmailId ?? '';
        const errors = { ...prev.errors };
        const key = fields[index].key;
        if (errors[key]) {
          const err = validateProfileField(fields[index], primaryEmailId, t);
          if (err) errors[key] = err;
          else delete errors[key];
        }
        const enableSave = Object.keys(collectErrors(fields, primaryEmailId, t)).length === 0;
        return { ...prev, fields, errors, enableSave };
      });
    },
    [t],
  );

  /** `displayValue` is the picked suggestion's label (Flutter controller.text = displayKey). */
  const updateField = useCallback(
    (index: number, value: string, displayValue: string = value) =>
      applyFieldChange(index, { value, displayValue }),
    [applyFieldChange],
  );

  const updateCountryCode = useCallback(
    (index: number, countryCode: string) => applyFieldChange(index, { countryCode }),
    [applyFieldChange],
  );

  /* ── Cancel — Flutter navigateTo(CoreRoute.viewJobs, isReplace: true) ── */
  const cancel = useCallback(() => {
    void analyticsService.logScreenView({ screenName: ROUTES.VIEW_JOBS });
    navigate(ROUTES.VIEW_JOBS, { replace: true });
  }, [navigate]);

  /** Leave edit mode without saving: reload the schema and candidate, dropping unsaved edits */
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const validateField = useCallback(
    (field: FldListState) =>
      validateProfileField(field, state.candidateDetails?.primaryEmailId ?? '', t),
    [state.candidateDetails, t],
  );

  /* ── Save profile ── */
  const handleSave = useCallback(async (): Promise<boolean> => {
    const errors = collectErrors(state.fields, state.candidateDetails?.primaryEmailId ?? '', t);
    if (Object.keys(errors).length > 0) {
      patch({ errors, errorMessage: FILL_ALL_MANDATORY_FIELDS });
      return false;
    }

    patch({ isBusy: true, errors: {}, errorMessage: '', successMessage: '' });
    try {
      const updated = buildCandidateDetails(state.candidateDetails, state.fields);
      await updateCandidateDetails(updated);
      void analyticsService.logProfileUpdate({
        userId: updated.displayId ?? '',
        fieldUpdated: 'profile_update',
      });
      // Flutter appStateNotifier.setCandidateDetails — header name / initials
      useAppStateStore.getState().setCandidateDetails(updated as Record<string, unknown>);
      patch({
        isBusy: false,
        candidateDetails: updated,
        successMessage: PROFILE_UPDATED,
      });
      return true;
    } catch {
      // Flutter _OnEditProfile failure only hides the loader
      patch({ isBusy: false });
      return false;
    }
  }, [state.fields, state.candidateDetails, patch, t]);

  /* ── Resume upload ── */
  const triggerFileSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';

      // Flutter pickFilesFromSystem (name / format) + the 5MB check in _SelectFilesFromSystem
      const fileError = validateResumeFile(file);
      if (fileError) {
        patch({ errorMessage: fileError });
        return;
      }

      patch({ isBusy: true, errorMessage: '' });
      try {
        await uploadResumeStandalone(file);
      } catch (error) {
        // Flutter _UploadResume: showMessage = ErrorConstants.emptyFile (204) or genericError
        const isEmptyFile = error instanceof Error && error.message === EMPTY_FILE_ERROR;
        patch({ isBusy: false, errorMessage: isEmptyFile ? EMPTY_FILE_ERROR : GENERIC_ERROR });
        return;
      }
      patch({ isBusy: false, successMessage: RESUME_UPLOADED });
      void analyticsService.logEvent('user_profile', 'upload_resume');
      // Flutter _GetUserDetails — refreshes the Documents block (failures leave it as is)
      try {
        const details = await getCandidateDetails();
        patch({ candidateDetails: details });
      } catch {
        /* keep the current details */
      }
    },
    [patch],
  );

  /* ── Resume download ── */
  const handleDownloadResume = useCallback(async () => {
    void analyticsService.logEvent('user_profile', 'download_resume');
    patch({ isBusy: true });
    try {
      const result = await downloadResume();
      const url = URL.createObjectURL(result.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = result.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      patch({ isBusy: false });
    } catch {
      // Flutter _DownloadResume failure only hides the loader
      patch({ isBusy: false });
    }
  }, [patch]);

  /* ── Location suggestions — Flutter locationSuggestions(otherFieldFetch: 'fqLocationName'):
        key = keyFqLocationName (the stored value), displayKey = fqLocationName ── */
  const fetchLocationSuggestions = useCallback(
    async (text: string): Promise<OptionData[]> => {
      try {
        return await getPlaceSuggestions({
          placeType: 'city',
          labelField: 'fqLocationName',
          key: text,
          page: 1,
        });
      } catch {
        return [];
      }
    },
    [],
  );

  /* ── Clear messages ── */
  const clearError = useCallback(() => patch({ errorMessage: '' }), [patch]);
  const clearSuccess = useCallback(() => patch({ successMessage: '' }), [patch]);

  /* ── Tenant config ── */
  const editProfileNote =
    ((preferences as Record<string, unknown>)?.['editProfileNote'] as string) ?? '';
  // Flutter Constants.supportEmailId = deleteAccountContent.emailId
  const supportEmailId = content?.contentSettings?.deleteAccountContent?.emailId ?? '';

  return {
    state,
    fileInputRef,
    actions: {
      updateField,
      updateCountryCode,
      cancel,
      reload,
      validateField,
      handleSave,
      triggerFileSelect,
      handleFileSelect,
      handleDownloadResume,
      fetchLocationSuggestions,
      clearError,
      clearSuccess,
    },
    config: {
      editProfileNote,
      supportEmailId,
    },
  };
}
